import { PAConstants, WMICMetadataUtil, CONSTANTS, JURISDICTIONS } from 'wmic-pe-portals-utils-js';
import _ from 'lodash';
import WMICRPCUtil from './WMICRPCUtil';

const isVehicleType = (vehicleVM, typeCode) => _.get(vehicleVM, 'vehicleType.value.code') === typeCode;
const isVehicleTypeMotorhome = vehicleVM => isVehicleType(vehicleVM, PAConstants.motorhomeVehicleType);
const isVehicleTypeTrailer = vehicleVM => isVehicleType(vehicleVM, PAConstants.trailerVehicleType);

const isCommerciallyRatedVehicleType = (businessSegmentType, vehicleType) =>
    businessSegmentType === PAConstants.vehicleBusinessSegmentCommercial &&
    ![
        PAConstants.snowVehicleType,
        PAConstants.atvVehicleType,
        PAConstants.motorcycleVehicleType,
        PAConstants.mopedVehicleType,
    ].includes(vehicleType);

const isCommerciallyRatedCommercialAuto = (isCommercialAuto, businessSegmentType, vehicleType) =>
    isCommercialAuto === true && isCommerciallyRatedVehicleType(businessSegmentType, vehicleType);

const vinSearchRequired = vehicle => {
    const hasVinOverride = vehicle.vinOverride_WMIC;
    const vinSearchAttempted = vehicle.vinSearchAttempted_WMIC;
    return !hasVinOverride && !vinSearchAttempted;
};

const updateVehicleList = async (jobVM, service, authHeader) => {
    const updatedVM = await service.postOnChange(jobVM.value, authHeader);

    const updatedVehicles = _.get(updatedVM, 'lobData.personalAuto.coverables.vehicles');
    const orderedVehicleList = _.orderBy(updatedVehicles, ['publicID'], ['asc']);
    _.set(jobVM.value, 'lobData.personalAuto.coverables.vehicles', orderedVehicleList);
};

const resetAntiTheftFields = vehicleVM => {
    const antiTheftDeviceEquipped = _.get(vehicleVM, 'antiTheftDeviceEquipped_WMIC.value');

    if (antiTheftDeviceEquipped === undefined) {
        _.set(vehicleVM, 'antiTheftDeviceMaker_Ext.value', null);
        _.set(vehicleVM, 'antiTheftDeviceInstallDate_Ext.value', null);
        _.set(vehicleVM, 'antiTheftDeviceType_WMIC.value', null);
        _.set(vehicleVM, 'antiTheftDesc_WMIC.value', null);
        return;
    }

    if (!WMICMetadataUtil.isVisible(vehicleVM, 'antiTheftDeviceEquipped_WMIC')) {
        _.set(vehicleVM, 'antiTheftDeviceEquipped_WMIC.value', null);
        _.set(vehicleVM, 'antiTheftDeviceMaker_Ext.value', null);
        _.set(vehicleVM, 'antiTheftDeviceInstallDate_Ext.value', null);
    } else if (!antiTheftDeviceEquipped || !WMICMetadataUtil.isVisible(vehicleVM, 'antiTheftDeviceMaker_Ext')) {
        _.set(vehicleVM, 'antiTheftDeviceMaker_Ext.value', null);
        _.set(vehicleVM, 'antiTheftDeviceInstallDate_Ext.value', null);
    }

    if (!antiTheftDeviceEquipped || !WMICMetadataUtil.isVisible(vehicleVM, 'antiTheftDeviceType_WMIC')) {
        _.set(vehicleVM, 'antiTheftDeviceType_WMIC.value', null);
        _.set(vehicleVM, 'antiTheftDesc_WMIC.value', null);
    }
};

const getVinOverrideReasonTypelist = ViewModelService =>
    ViewModelService.productMetadata.get('pc').types.getTypelist('VinOverrideReason_WMIC');

const getVinOverrideReasonFallbackValue = (vehicleVM, ViewModelService) => {
    const validBluepassReasonCodes = [
        CONSTANTS.VIN_OVERRIDE_REASONS.COMMERCIAL,
        CONSTANTS.VIN_OVERRIDE_REASONS.OLDER,
        CONSTANTS.VIN_OVERRIDE_REASONS.VIN_BY_PASS,
    ];

    if (!isVehicleTypeMotorhome(vehicleVM) && !isVehicleTypeTrailer(vehicleVM)) {
        if (
            vehicleVM?.vinOverrideReason_WMIC?.value &&
            !validBluepassReasonCodes.includes(vehicleVM.vinOverrideReason_WMIC.value.code)
        ) {
            return getVinOverrideReasonTypelist(ViewModelService)?.getCode(CONSTANTS.VIN_OVERRIDE_REASONS.VIN_BY_PASS);
        }
    }

    return vehicleVM?.vinOverrideReason_WMIC?.value;
};

const formatVehicleDisplayLabel = vehicle => {
    const vehicleNumber = vehicle?.vehicleNumber_WMIC || vehicle?.vehicleNumber_Ext;

    return (
        (vehicleNumber ? `#${vehicleNumber} : ` : '') +
        (vehicle?.year ? `${vehicle.year} ` : '') +
        (vehicle?.make ? `${vehicle.make} ` : '') +
        (vehicle?.model ? `${vehicle.model} ` : '') +
        (vehicle?.vin ? `(${vehicle.vin})` : '')
    );
};

const formatVehicleDisplayLabelFromVehiclePublicID = (publicID, vehicles) => {
    const vehicle = vehicles?.find(vehicle => vehicle.publicID === publicID);
    return formatVehicleDisplayLabel(vehicle);
};

const setVehicleInspectionRequired = (vehicleVM, baseState) => {
    const vehicleTypes = [PAConstants.personalAutoVehicleType, PAConstants.motorhomeVehicleType];
    const jurisdictions = [JURISDICTIONS.ALBERTA, JURISDICTIONS.YUKON];
    const today = new Date();
    const vehicleAge = today.getFullYear() - vehicleVM.year.value;
    if (
        vehicleTypes.includes(vehicleVM?.vehicleType?.value?.code) &&
        jurisdictions.includes(baseState) &&
        vehicleAge >= PAConstants.minimumVehicleAgeForInspection &&
        vehicleVM?.year?.value >= PAConstants.minVehicleYear
    ) {
        vehicleVM.vehicleInspectionRequired_WMIC = true;
    } else {
        vehicleVM.vehicleInspectionRequired_WMIC = false;
    }
};

const isVehicleCustomizationTotalAmountVisible = vehicleVM =>
    !vehicleVM?.customizationsExist_Ext?.aspects?.ocular ||
    !!vehicleVM?.customizationsExist_Ext?.value ||
    vehicleVM?.customizations_WMIC?.value?.length > 0;

const getDefaultBusinessSegment = (jobVM, vehicleType) => {
    const isRPC2172Effective = WMICRPCUtil.getIsRPCEffective(
        jobVM?.baseData?.baseState?.value?.code,
        jobVM?.baseData?.rateAsOfDate?.value,
        '2172'
    );

    const isInvalidCommercialVehicleType = [
        PAConstants.motorcycleVehicleType,
        PAConstants.motorhomeVehicleType,
    ].includes(vehicleType);

    const shouldDefaultToCommercial =
        isRPC2172Effective && jobVM?.lobData?.personalAuto?.isCommercialAutoIndicator_Ext?.value && !isInvalidCommercialVehicleType;

    return shouldDefaultToCommercial
        ? CONSTANTS.BUSINESS_SEGMENT_CODES.COMMERCIAL
        : CONSTANTS.BUSINESS_SEGMENT_CODES.PERSONAL;
};

const getDefaultPrimaryUse = (isCommercialAuto, businessSegmentType, vehicleType) => {
    if (isCommerciallyRatedCommercialAuto(isCommercialAuto, businessSegmentType, vehicleType)) {
        return CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS;
    }
    return businessSegmentType === PAConstants.vehicleBusinessSegmentCommercial
        ? CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS
        : CONSTANTS.VEHICLE_PRIMARY_USE.PLEASURE;
};

export default {
    isCommerciallyRatedVehicleType,
    isCommerciallyRatedCommercialAuto,
    resetAntiTheftFields,
    updateVehicleList,
    vinSearchRequired,
    getVinOverrideReasonFallbackValue,
    formatVehicleDisplayLabel,
    formatVehicleDisplayLabelFromVehiclePublicID,
    setVehicleInspectionRequired,
    isVehicleCustomizationTotalAmountVisible,
    getDefaultBusinessSegment,
    getDefaultPrimaryUse,
};
